import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get values() {
    return {
      updateOrderUrl: String,
    }
  }

  static get targets() {
    return ["column", "table", "moveLeftButton", "moveRightButton"]
  }

  connect() {
    const firstDraggableColumn = this.columnTargets.find((column) => column.draggable)
    this.firstDraggableColumnIndex = firstDraggableColumn.cellIndex
  }

  moveLeft(event) {
    const currentColumn = event.target.closest("[data-column-name]")
    const columnName = currentColumn.dataset.columnName
    const currentOrder = this._getCurrentColumnOrder()
    const currentIndex = currentOrder.indexOf(columnName)

    if (currentIndex > 0) {
      // Swap with previous column
      ;[currentOrder[currentIndex - 1], currentOrder[currentIndex]] = [
        currentOrder[currentIndex],
        currentOrder[currentIndex - 1],
      ]

      this._reorderRows(columnName, currentOrder, true)
      this._hideMoveButtons(currentOrder)
      new bootstrap.Dropdown(event.target.closest(".dropdown")).hide()
      this._saveColumnOrder(currentOrder)
    }
  }

  moveRight(event) {
    const currentColumn = event.target.closest("[data-column-name]")
    const columnName = currentColumn.dataset.columnName
    const currentOrder = this._getCurrentColumnOrder()
    const currentIndex = currentOrder.indexOf(columnName)

    if (currentIndex < currentOrder.length - 1) {
      // Swap with next column
      ;[currentOrder[currentIndex + 1], currentOrder[currentIndex]] = [
        currentOrder[currentIndex],
        currentOrder[currentIndex + 1],
      ]

      this._reorderRows(columnName, currentOrder, true)
      this._hideMoveButtons(currentOrder)
      new bootstrap.Dropdown(event.target.closest(".dropdown")).hide()
      this._saveColumnOrder(currentOrder)
    }
  }

  handleDragStart(event) {
    event.target.classList.add("dragging")
    event.dataTransfer.setData("text/plain", event.target.dataset.columnName)
  }

  handleDragOver(event) {
    event.preventDefault()
    const draggingElement = this.element.querySelector(".dragging")
    const currentElement = event.target.closest("[data-column-name]")

    if (!draggingElement || !currentElement) return
    if (!currentElement.draggable) return

    const rect = currentElement.getBoundingClientRect()
    const afterElement = event.clientX - rect.left > rect.width / 2

    if (afterElement) {
      currentElement.parentNode.insertBefore(draggingElement, currentElement.nextSibling)
    } else {
      currentElement.parentNode.insertBefore(draggingElement, currentElement)
    }
  }

  handleDragEnd(event) {
    event.target.classList.remove("dragging")
    const columnName = event.target.dataset.columnName
    const newOrder = this._getCurrentColumnOrder()

    this._reorderRows(columnName, newOrder)
    this._hideMoveButtons(newOrder)
    this._saveColumnOrder(newOrder)
  }

  _getCurrentColumnOrder() {
    return this.columnTargets.filter((column) => column.draggable).map((column) => column.dataset.columnName)
  }

  _reorderRows(columnName, newOrder, includeHeader = false) {
    const newColumnPosition = newOrder.indexOf(columnName) + this.firstDraggableColumnIndex
    let cellsToMove

    if (includeHeader) {
      cellsToMove = Array.from(this.tableTarget.rows)
        .map((row) => Array.from(row.cells))
        .flat()
        .filter((cell) => cell.dataset.columnName === columnName)
    } else {
      cellsToMove = Array.from(this.tableTarget.tBodies[0].rows)
        .map((row) => Array.from(row.cells))
        .flat()
        .filter((cell) => cell.dataset.columnName === columnName)
    }

    cellsToMove.forEach((cell) => {
      const row = cell.parentElement
      // Remove the cell from its current position
      cell.remove()
      // Insert the cell at the new position
      if (newColumnPosition >= row.cells.length) {
        row.appendChild(cell)
      } else {
        row.insertBefore(cell, row.cells[newColumnPosition])
      }
    })
  }

  _hideMoveButtons(newOrder) {
    this.columnTargets.forEach((column) => {
      const newColumnIndex = newOrder.indexOf(column.dataset.columnName)
      const moveLeftButton = column.querySelector("li[data-data-table--column-reorder-target='moveLeftButton']")
      const moveRightButton = column.querySelector("li[data-data-table--column-reorder-target='moveRightButton']")

      if (newColumnIndex === 0) {
        moveLeftButton.classList.add("d-none")
      } else {
        moveLeftButton.classList.remove("d-none")
      }

      if (newColumnIndex === newOrder.length - 1) {
        moveRightButton.classList.add("d-none")
      } else {
        moveRightButton.classList.remove("d-none")
      }
    })
  }

  async _saveColumnOrder(newOrder) {
    // Create URL object from the base URL
    const url = new URL(this.updateOrderUrlValue, window.location.origin)

    // Add query parameters
    newOrder.forEach((columnName) => {
      url.searchParams.append(`column[${columnName}]`, "true")
    })

    // Make the fetch request
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token,
      },
    })
  }
}
